/**
 * OverlapReviewDashboard — Full-page review queue for CONFIRMED overlaps.
 * Blueprint: F-06 — new UI component, Reviewer + Claims Admin access only.
 *
 * Shows all overlaps with status=CONFIRMED pending reviewed_by_user_id.
 * Supports: one-click Accept, one-click Reject, multi-select bulk accept.
 *
 * TODO: Source from GET /overlap/review-queue (new endpoint F-06).
 * TODO: Bulk accept via POST /overlap/bulk-accept with selected IDs.
 * TODO: Filter by tort, confidence score, rule applied.
 * TODO: Redirect CLIENT_USER to 403 — this page is Reviewer/CLAIMS_ADMIN only (F-04).
 */
import React, { useEffect, useState } from 'react';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { CheckCircle, GitMerge } from 'lucide-react';
import { useOverlap } from '@/hooks/cmm/useOverlap';
import { OverlapCard } from '@/components/cmm/OverlapCard';

const BREADCRUMBS = [
  { label: 'Claimants', href: '/claimants' },
  { label: 'Overlap Review' },
];

const OverlapReviewDashboard: React.FC = () => {
  const [selected, setSelected] = useState<string[]>([]);
  const { reviewQueue, isLoading, fetchReviewQueue, acceptOverlap, rejectOverlap, bulkAccept } = useOverlap();

  useEffect(() => { fetchReviewQueue(); }, []);

  const toggleSelect = (id: string) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleBulkAccept = async () => {
    await bulkAccept(selected);
    setSelected([]);
    fetchReviewQueue();
  };

  const headerActions = selected.length > 0 ? (
    <button onClick={handleBulkAccept}
      className="flex items-center gap-1.5 text-sm font-medium text-white bg-green-600 rounded-md px-4 py-1.5 hover:bg-green-700">
      <CheckCircle className="w-4 h-4" /> Accept {selected.length} Selected
    </button>
  ) : undefined;

  return (
    <BaseLayout
      title="Overlap Review Queue"
      subtitle="CONFIRMED overlaps awaiting human review"
      breadcrumbs={BREADCRUMBS}
      headerActions={headerActions}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Loading review queue...</div>
      ) : reviewQueue.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400">
          <GitMerge className="w-10 h-10 mb-2 opacity-30" />
          <p className="text-sm">No overlaps pending review</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviewQueue.map(overlap => (
            <div key={overlap.id} className="flex items-start gap-3">
              <input type="checkbox"
                checked={selected.includes(overlap.id)}
                onChange={() => toggleSelect(overlap.id)}
                className="mt-4 rounded border-gray-300"
              />
              <div className="flex-1">
                <OverlapCard
                  overlap={overlap}
                  onAccept={async id => { await acceptOverlap(id); fetchReviewQueue(); }}
                  onReject={async id => { await rejectOverlap(id); fetchReviewQueue(); }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </BaseLayout>
  );
};

export default OverlapReviewDashboard;
